import React, { useEffect, useRef, useState } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { ClientSelector } from '@/components/ClientSelector';
import { useClient } from '@/contexts/ClientContext';
import { useViewMode } from '@/contexts/ViewModeContext';
import { usePermissions } from '@/hooks/usePermissions';
import AuditLogModal from '@/components/audit/AuditLogModal';
import { clearAuditUrlParams, hasAuditUrlFlag } from '@/components/audit/auditState';
import UserManagementModal from '@/components/admin/UserManagementModal';
import NotificationCenter from './NotificationCenter';

const Header: React.FC = () => {
  const { user, logout } = useAuth();
  const { selectedClient, setSelectedClient } = useClient();
  const { isAdminView } = useViewMode();
  const { hasPermission, role } = usePermissions();
  const isAdmin = role === 'Admin';
  const canViewAuditConsole = hasPermission('audit.view_system');
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [showAuditModal, setShowAuditModal] = useState(false);
  const [showUserManagement, setShowUserManagement] = useState(false);
  const menuRef = useRef<HTMLDivElement | null>(null);
  const auditAutoOpened = useRef(false);
  const auditWasOpen = useRef(false);

  // A shared/reloaded audit link (`?audit=1&…`) opens the console on load — but only for a
  // user who holds the same permission that gates the Audit Log menu item. Once per page load.
  useEffect(() => {
    if (auditAutoOpened.current || !user || !canViewAuditConsole) return;
    if (!hasAuditUrlFlag()) return;
    auditAutoOpened.current = true;
    setShowAuditModal(true);
  }, [user, canViewAuditConsole]);

  // Closing the console by any path (close button, Escape, backdrop) drops its URL params.
  useEffect(() => {
    if (auditWasOpen.current && !showAuditModal) clearAuditUrlParams();
    auditWasOpen.current = showAuditModal;
  }, [showAuditModal]);

  useEffect(() => {
    if (!isMenuOpen) {
      return;
    }

    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isMenuOpen]);

  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsMenuOpen(false);
        setShowAuditModal(false);
        setShowUserManagement(false);
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, []);

  const handleLogout = async () => {
    setIsMenuOpen(false);
    try {
      // The Sign Out button is the one logout a human deliberately asks for, so it is
      // the one that retires the Google grant. Passed explicitly rather than relying
      // on the default, so the intent survives a future change to that default.
      await logout('user');
      window.location.href = '/login';
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };

  const handleAuditModalOpen = () => {
    setIsMenuOpen(false);
    setShowAuditModal(true);
  };

  const handleUserManagementOpen = () => {
    setIsMenuOpen(false);
    setShowUserManagement(true);
  };

  const renderAvatar = () => {
    if (!user) {
      return null;
    }

    if (user.picture) {
      return (
        <img
          src={user.picture}
          alt={user.name}
          className="h-7 w-7 rounded-full border border-gray-200 object-cover"
          referrerPolicy="no-referrer"
        />
      );
    }

    return (
      <div className="flex h-7 w-7 items-center justify-center rounded-full bg-blue-600 text-xs font-medium text-white">
        {user.name.charAt(0).toUpperCase()}
      </div>
    );
  };

  return (
    <header className="border-b border-gray-200 bg-white relative z-30">
      <div className="px-3 py-1.5">
        <div className="flex items-center">
          {/* Left section - Logo */}
          <div className="flex-1 flex items-center gap-3">
            <img
              src="/provia-logo.png"
              alt="Provia RCM Partners"
              className="h-8 w-auto"
            />
          </div>

          {/* Center section - Client Selector */}
          <div className="flex-1 flex justify-center">
            {user && (
              <ClientSelector
                selectedClient={selectedClient}
                onClientChange={setSelectedClient}
              />
            )}
          </div>

          {/* Right section - Notifications and User Menu */}
          <div className="flex-1 flex items-center justify-end gap-4">
          {user && <NotificationCenter />}

          {user && (
            <div className="relative" ref={menuRef}>
              <button
                type="button"
                onClick={() => setIsMenuOpen((previous) => !previous)}
                className="flex items-center gap-2 rounded-lg border border-gray-200 px-2 py-1.5 text-left transition hover:bg-gray-50"
                aria-haspopup="true"
                aria-expanded={isMenuOpen}
              >
                <div className="hidden sm:block max-w-[140px]">
                  <p className="text-sm font-medium text-gray-900 truncate">{user.name}</p>
                  <p className="text-xs text-gray-500 truncate">{user.email}</p>
                </div>
                <div className="flex items-center gap-1.5">
                  {renderAvatar()}
                  <i className={`fas ${isMenuOpen ? 'fa-chevron-up' : 'fa-chevron-down'} text-gray-400 text-xs`}></i>
                </div>
              </button>

              {isMenuOpen && (
                <div className="absolute right-0 z-50 mt-2 w-64 rounded-lg border border-gray-200 bg-white shadow-xl">
                  <div className="border-b border-gray-200 px-4 py-3">
                    <p className="text-sm font-semibold text-gray-900">{user.name}</p>
                    <p className="text-xs text-gray-500">{user.email}</p>
                    {selectedClient && (
                      <p className="mt-1 text-xs text-gray-400">Client: {selectedClient.name}</p>
                    )}
                  </div>
                  <div className="py-1 text-sm">
                    {/* Admin-only menu items */}
                    {isAdmin && (
                      <button
                        type="button"
                        onClick={handleUserManagementOpen}
                        className="flex w-full items-center gap-2 px-4 py-2 text-gray-700 transition hover:bg-gray-100"
                      >
                        <i className="fas fa-users-cog text-gray-400"></i>
                        Access Management
                      </button>
                    )}
                    {/* Audit Log — the single audit surface (full console in a modal) */}
                    {canViewAuditConsole && (
                      <button
                        type="button"
                        onClick={handleAuditModalOpen}
                        className="flex w-full items-center gap-2 px-4 py-2 text-gray-700 transition hover:bg-gray-100"
                      >
                        <i className="fas fa-history text-gray-400"></i>
                        Audit Log
                      </button>
                    )}
                    <button
                      type="button"
                      onClick={handleLogout}
                      className="flex w-full items-center gap-2 px-4 py-2 text-gray-700 transition hover:bg-gray-100"
                    >
                      <i className="fas fa-sign-out-alt text-gray-400"></i>
                      Logout
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
          </div>
        </div>
      </div>

      {user && (
        <AuditLogModal
          isOpen={showAuditModal}
          onClose={() => setShowAuditModal(false)}
          clientId={selectedClient?.id ?? null}
          clientName={selectedClient?.name}
          userId={null}
          userName={user.name}
          userEmail={user.email}
        />
      )}

      {user && isAdmin && (
        <UserManagementModal
          isOpen={showUserManagement}
          onClose={() => setShowUserManagement(false)}
        />
      )}
    </header>
  );
};

export default Header;

